import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface ColorPickerDragLockContextValue {
  readonly registerOpenColorPicker: () => () => void;
  readonly isColorPickerOpen: boolean;
}

const ColorPickerDragLockContext =
  createContext<ColorPickerDragLockContextValue | null>(null);

export function ColorPickerDragLockProvider({
  children,
}: {
  readonly children: React.ReactNode;
}): JSX.Element {
  const [openCount, setOpenCount] = useState(0);

  const registerOpenColorPicker = useCallback(() => {
    setOpenCount((count) => count + 1);
    return () => {
      setOpenCount((count) => Math.max(0, count - 1));
    };
  }, []);

  const value = useMemo(
    () => ({
      registerOpenColorPicker,
      isColorPickerOpen: openCount > 0,
    }),
    [registerOpenColorPicker, openCount],
  );

  return (
    <ColorPickerDragLockContext.Provider value={value}>
      {children}
    </ColorPickerDragLockContext.Provider>
  );
}

export function useColorPickerDragLock(): ColorPickerDragLockContextValue {
  const context = useContext(ColorPickerDragLockContext);
  if (!context) {
    return {
      registerOpenColorPicker: () => () => undefined,
      isColorPickerOpen: false,
    };
  }
  return context;
}
